import React, { Dispatch, SetStateAction, useEffect } from 'react';
import TextareaAutosize from 'react-textarea-autosize';
import { FaExclamationCircle } from 'react-icons/fa';
import { format } from 'date-fns';

import { UseOutletContextSale } from '../Sale';
import { TreasuryAccount } from '../../../../models/treasury.account.model';
import { DunningPolicy } from '../../../../models/settings.dunning-policies.model';
import { StateLanguage } from '../../../../languages/config/StateLanguage';
import { legendInvalid, legendValid } from '../../../../utils/legend.data.util';
import { uploadTooltip } from '../../../../utils/tooltip.util';
import { evaluateLegendRequired } from '../../../../scripts/validate.legend.script';
import { expressions } from '../../../../libraries/regular.expressions.library';

import AppLegend from '../../../../components/element/Legend';
import AppInputContact from '../../../../components/element/InputContact';
import AppInputDatePayment from '../../../../components/element/InputDatePayment';
import AppInputCurrency from '../../../../components/element/InputCurrency';
import AppInputTreasuryAccount from '../../../../components/element/InputTreasuryAccount';
import AppInputLanguage from '../../../../components/element/InputLanguage';
import AppInputQrType from '../../../../components/element/InputQrType';
import AppInputDicount from '../../../../components/element/InputDiscount';
import AppInputForeignCurrency, { ForeignCurrencyState } from '../../../../components/element/InputForeignCurrency';

export interface AppSaleFormCreateProps {
  sale: string,
  customer: {value: string, valid: boolean, object: any},
  concerning: {value: string, valid: boolean},
  date: {value: string, valid: boolean},
  validUntil: {value: string, valid: boolean},
  deadline: {value: string, valid: boolean, object: any},
  currency: {value: string, valid: boolean, object: any},
  treasuryAccount: {value: string, valid: boolean, object: any},
  language: {value: string, valid: boolean},
  qrType?: {value: string, valid: boolean},
  discountType: {value: string, valid: boolean},
  discountValue: {value: string, valid: boolean},
  foreignCurrency?: ForeignCurrencyState,
  setCustomer: Dispatch<SetStateAction<{value: string, valid: boolean, object: any}>>,
  setConcerning: Dispatch<SetStateAction<{value: string, valid: boolean}>>,
  setDate: Dispatch<SetStateAction<{value: string, valid: boolean}>>,
  setValidUntil: Dispatch<SetStateAction<{value: string, valid: boolean}>>,
  setDeadline: Dispatch<SetStateAction<{value: string, valid: boolean, object: any}>>,
  setCurrency: Dispatch<SetStateAction<{value: string, valid: boolean, object: any}>>,
  setTreasuryAccount: Dispatch<SetStateAction<{value: string, valid: boolean, object: any}>>,
  setLanguage: Dispatch<SetStateAction<{value: string, valid: boolean}>>,
  setQrType?: Dispatch<SetStateAction<{value: string, valid: boolean}>>,
  setDiscountType: Dispatch<SetStateAction<{value: string, valid: boolean}>>,
  setDiscountValue: Dispatch<SetStateAction<{value: string, valid: boolean}>>,
  setForeignCurrency?: Dispatch<SetStateAction<ForeignCurrencyState>>,
  dunningPolicyId?: string,
  setDunningPolicyId?: Dispatch<SetStateAction<string>>,
  dunningPolicies?: DunningPolicy[]
};

const AppSaleFormCreate: React.FunctionComponent<AppSaleFormCreateProps> = ({sale, customer, concerning, date, validUntil, deadline, currency, treasuryAccount, language, qrType, discountType, discountValue, foreignCurrency, setCustomer, setConcerning, setDate, setValidUntil, setDeadline, setCurrency, setTreasuryAccount, setLanguage, setQrType, setDiscountType, setDiscountValue, setForeignCurrency, dunningPolicyId, setDunningPolicyId, dunningPolicies}) => {
  const {appServiceForUser, companyForUser, settingsForCompany, treasuryAccountsForCompany, contactsForCompany, loadSettingsForCompany, loadContactsForCompany} = UseOutletContextSale()
  const {lang} = StateLanguage()

  const handleChangeConcerning = (event: React.ChangeEvent <HTMLFormElement | HTMLTextAreaElement>) => {
    setConcerning({...concerning, value: event.target.value})
  }

  const handleChangeDate = (event: React.ChangeEvent <HTMLFormElement | HTMLInputElement>) => {
    if (event.target.value.length > 0) {
      setDate({value: event.target.value, valid: true})
      legendValid('legend-date-required')
    } else {
      setDate({value: event.target.value, valid: false})
      legendInvalid('legend-date-required')
    }
  }

  const validateConcerning = () => {
    evaluateLegendRequired(expressions.text, concerning, setConcerning, 'legend-concerning-valid', 'legend-concerning-required')
  }

  function currentDate(): string {
    return format((new Date()), 'yyyy-MM-dd')
  }

  useEffect( () => {
    setCustomer({value: '', valid: false, object: null})
    setCurrency({value: '', valid: false, object: null})
    setLanguage({value: '', valid: false})

    if (companyForUser?.currency) {
      setCurrency({value: companyForUser.currency?.code, object: companyForUser.currency, valid: true})
    }
    if (companyForUser?.language) {
      setLanguage({value: companyForUser.language, valid: true})
    }

  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyForUser])

  useEffect( () => {
    setTreasuryAccount({value: '', valid: false, object: null})

    if (treasuryAccountsForCompany) {
      if (treasuryAccountsForCompany.some((item) => (item.default))) {
        let defaultTreasuryAccount = treasuryAccountsForCompany.find(item => item.default) as TreasuryAccount
        setTreasuryAccount({value: defaultTreasuryAccount.id, object: defaultTreasuryAccount, valid: true})
      }
    }

  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [treasuryAccountsForCompany])

  useEffect( () => {
    setDate({value: format((new Date()), 'yyyy-MM-dd'), valid: true})
    setValidUntil({value: format((new Date()), 'yyyy-MM-dd'), valid: true})
    setDiscountType({value: 'none', valid: true})
    setDiscountValue({value: '', valid: true})
    uploadTooltip()

  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  return (
    <>
      { appServiceForUser && companyForUser && settingsForCompany && treasuryAccountsForCompany && contactsForCompany &&
        <div className="card card-flush mb-5">
          <div className="card-header">
            <div className="card-title">
              {lang.labels[`new${sale.charAt(0).toUpperCase()}${sale.slice(1)}` as keyof typeof lang.labels]}
            </div>
          </div>
          <div className="card-body pt-0">
            <div className="fw-bold fs-6 mb-5">{lang.labels[`descriptionCreate${sale.charAt(0).toUpperCase()}${sale.slice(1)}` as keyof typeof lang.labels]}</div>
            <div className="row mb-0 mb-lg-2">
              <label className="col-lg-3 col-form-label required">{lang.labels.customer}</label>
              <div className="col-lg-9">
                <AppInputContact appServiceForUser={appServiceForUser} companyForUser={companyForUser} settingsForCompany={settingsForCompany} contact={customer} setContact={setCustomer} setLanguage={setLanguage} setQrTtype={setQrType} loadContactsForCompany={loadContactsForCompany}></AppInputContact>
              </div>
            </div>
            <div className="row mb-0 mb-lg-2">
              <label className="col-lg-3 col-form-label required">{lang.labels.concerning}</label>
              <div className="col-lg-9">
                <TextareaAutosize className="form-control" name="concerning" minRows={2} value={concerning.value} onChange={handleChangeConcerning} onKeyUp={validateConcerning} onBlur={validateConcerning} />
                <AppLegend attribute={{check: concerning.valid, name: "concerning", index: null, sub_index: null}} container={{valid: true, required: true, identical: false, exists: false}}></AppLegend>
              </div>
            </div>
            <div className="row mb-0 mb-lg-2">
              <label className="col-lg-3 col-form-label">
                <div className="d-flex align-items-center">
                  <span className="required">{lang.labels.creationDate}</span>
                  <i className="ms-2 fs-8" data-bs-toggle="tooltip" data-bs-trigger="hover" data-bs-original-title={lang.labels.dateOfDocument}><FaExclamationCircle /></i>
                </div>
              </label>
              <div className="col-lg-9">
                <input className="form-control" type="date" name="date" max={currentDate()} value={date.value.substring(0, 10)} onChange={handleChangeDate} />
                <AppLegend attribute={{check: date.valid, name: "date", index: null, sub_index: null}} container={{valid: false, required: true, identical: false, exists: false}}></AppLegend>
              </div>
            </div>
            <div className="row mb-0 mb-lg-2">
              <label className="col-lg-3 col-form-label">
                <div className="d-flex align-items-center">
                  <span className="required">{lang.labels.expirationDate}</span>
                  <i className="ms-2 fs-8" data-bs-toggle="tooltip" data-bs-trigger="hover" data-bs-original-title={lang.labels.enterADateOrSelectADefaultPaymentDeadline}><FaExclamationCircle /></i>
                </div>
              </label>
              <div className="col-lg-9">
                <AppInputDatePayment companyForUser={companyForUser} settingsForCompany={settingsForCompany} validUntil={validUntil} deadline={deadline} setValidUntil={setValidUntil} setDeadline={setDeadline} loadSettingsForCompany={loadSettingsForCompany}></AppInputDatePayment>
              </div>
            </div>
            <div className="row mb-0 mb-lg-2">
              <label className="col-lg-3 col-form-label">
                <div className="d-flex align-items-center">
                  <span className="required">{lang.labels.currency}</span>
                  <i className="ms-2 fs-8" data-bs-toggle="tooltip" data-bs-trigger="hover" data-bs-original-title={lang.labels.theDefaultCurrencyIsTheCompanys}><FaExclamationCircle /></i>
                </div>
              </label>
              <div className="col-lg-9">
                <AppInputCurrency currency={currency} setCurrency={setCurrency}></AppInputCurrency>
              </div>
            </div>
            { currency.value && companyForUser && currency.value !== companyForUser.currency?.code && foreignCurrency && setForeignCurrency &&
              <div className="row mb-0 mb-lg-2">
                <label className="col-lg-3 col-form-label">{lang.labels.exchangeRate}</label>
                <div className="col-lg-9">
                  <AppInputForeignCurrency companyId={companyForUser.id} companyCurrencyCode={companyForUser.currency?.code} selectedCurrency={currency.object} foreignCurrency={foreignCurrency} setForeignCurrency={setForeignCurrency}></AppInputForeignCurrency>
                </div>
              </div>
            }
            { sale === 'invoice' &&
              <div className="row mb-0 mb-lg-2">
                <label className="col-lg-3 col-form-label">
                  <div className="d-flex align-items-center">
                    <span className="required">{lang.labels.treasuryAccount}</span>
                    <i className="ms-2 fs-8" data-bs-toggle="tooltip" data-bs-trigger="hover" data-bs-original-title={lang.labels.theDefaultBankAccountIsTheCompanys}><FaExclamationCircle /></i>
                  </div>
                </label>
                <div className="col-lg-9">
                  <AppInputTreasuryAccount solid={true} treasuryAccountsForCompany={treasuryAccountsForCompany} treasuryAccount={treasuryAccount} setTreasuryAccount={setTreasuryAccount}></AppInputTreasuryAccount>
                </div>
              </div>
            }
            <div className="row mb-0 mb-lg-2">
              <label className="col-lg-3 col-form-label">
                <div className="d-flex align-items-center">
                  <span className="required">{lang.labels.language}</span>
                  <i className="ms-2 fs-8" data-bs-toggle="tooltip" data-bs-trigger="hover" data-bs-original-title={lang.labels.theDefaultLanguageToDownloadIsTheClients}><FaExclamationCircle /></i>
                </div>
              </label>
              <div className="col-lg-9">
                <AppInputLanguage language={language} setLanguage={setLanguage}></AppInputLanguage>
              </div>
            </div>
            { qrType && setQrType &&
              <div className="row mb-0 mb-lg-2">
                <label className="col-lg-3 col-form-label">
                  <div className="d-flex align-items-center">
                    <span className="required">{lang.labels.qrType}</span>
                    <i className="ms-2 fs-8" data-bs-toggle="tooltip" data-bs-trigger="hover" data-bs-original-title={lang.labels.theDefaultShapeOrVersionOfAQrInvoiceToDownloadIsTheClients}><FaExclamationCircle /></i>
                  </div>
                </label>
                <div className="col-lg-9">
                  <AppInputQrType qrType={qrType} setQrType={setQrType}></AppInputQrType>
                </div>
              </div>
            }
            { sale === 'invoice' && dunningPolicies && setDunningPolicyId &&
              <div className="row mb-0 mb-lg-2">
                <label className="col-lg-3 col-form-label">
                  <div className="d-flex align-items-center">
                    <span>{lang.labels.dunningPolicy}</span>
                    <i className="ms-2 fs-8" data-bs-toggle="tooltip" data-bs-trigger="hover" data-bs-original-title={lang.labels.dunningPolicyHint}><FaExclamationCircle /></i>
                  </div>
                </label>
                <div className="col-lg-9">
                  <select className="form-select" value={dunningPolicyId || ''} onChange={(e) => setDunningPolicyId(e.target.value)}>
                    <option value="">{lang.labels.noDunning}</option>
                    {dunningPolicies.map((policy) => (
                      <option key={policy.id} value={policy.id}>
                        {policy.label}{policy.is_default ? ` (${lang.labels.setAsDefault})` : ''}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            }
            <div className="row d-none">
              <label className="col-lg-3 col-form-label">
                <div className="d-flex align-items-center">
                  <span className="required">{lang.labels.discount}</span>
                  <i className="ms-2 fs-8" data-bs-toggle="tooltip" data-bs-trigger="hover" data-bs-original-title={lang.labels.selectOptionAddAmountPercentageDesired}><FaExclamationCircle /></i>
                </div>
              </label>
              <div className="col-lg-9">
                <AppInputDicount discountType={discountType} discountValue={discountValue} setDiscountType={setDiscountType} setDiscountValue={setDiscountValue}></AppInputDicount>
              </div>
            </div>
          </div>
        </div>
      }
    </>
  )
};

export default AppSaleFormCreate;
